import { Permission, notEqual } from '@cims-online/shared'
import { EditRowActionOptions } from 'components/CIMSTable/CIMSTableContext/types/actions/row'
import { useSubmittalTrackerContext } from '../../../../SubmittalTrackerContext'
import { Submittal } from '../../../../SubmittalTrackerContext/types'
import { EditModalBody } from './components/EditSubmittalModalBody'
import { useEditCasSharePointReferenceMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { toMoment } from 'utils/toMoment'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { EditSubmittalModalValues } from './components/EditSubmittalModalBody/types'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { toLabeledValues } from 'components/CIMSSelect/utils/labeledValues'
import { Moment } from 'moment'

export function useEditOptions(): EditRowActionOptions<
  Submittal,
  EditSubmittalModalValues
> {
  const { refetch, edit } = useSubmittalTrackerContext()
  /**
   * don't add an onCompleted notification callback here
   *
   * we're unable to selectively call the edit mutation with custom fields. if
   * you add a onCompleted notification here, you get a client side notification
   * for both the package name/number success and edit success, even if you didn't
   * actually edit a non-name/number field
   */
  const [editSharePointReference] = useEditCasSharePointReferenceMutation(
    DefaultMutationHookOptions
  )

  const { toLabeledValues: toPayItems, fromLabeledValues: fromPayItems } =
    usePayItemSelectContext()
  const { toCustomFieldEditorValue, toMutateCustomFieldsInput } =
    useProjectCustomFieldsContext()
  return {
    permission: Permission.EditConstructionAdminSubmittal,
    modal: {
      recordName: 'packageName',
      body: (record) => <EditModalBody {...record} />,
      initialValues: ({
        packageNumber,
        packageName,
        quantity,
        units,
        requestedReviewDate,
        contractualReviewDate,
        customFields,
        externalLinks,
        casToPayItemNoGeometries,
        casToCASRoutingGroups,
        type,
        subtype,
      }) => ({
        packageNumber,
        packageName,
        type: { label: type.name, value: type.id, tooltip: type.description },
        subtype: subtype
          ? {
              label: subtype.name,
              value: subtype.id,
              tooltip: subtype.description,
            }
          : null,
        quantity,
        units,
        requestedReviewDate: toMoment(requestedReviewDate) as Moment,
        contractualReviewDate: toMoment(contractualReviewDate) ?? null,
        routingGroups: toLabeledValues(casToCASRoutingGroups, {
          type: 'callback',
          callback: ({ routingGroup: { id, name, description } }) => ({
            label: name,
            value: id,
            tooltip: description,
          }),
        }),
        externalLinks: externalLinks.map(({ __typename, ...link }) => link),
        payItems: toPayItems(
          casToPayItemNoGeometries.map(({ payItem }) => payItem)
        ),
        customFields: toCustomFieldEditorValue(customFields),
      }),
      onOk: async (
        { id, casToPayItemNoGeometries, casToCASRoutingGroups, ...submittal },
        {
          packageName,
          packageNumber,
          type,
          subtype,
          quantity,
          units,
          requestedReviewDate,
          contractualReviewDate,
          routingGroups,
          externalLinks,
          payItems,
          customFields,
        }
      ) => {
        if (
          notEqual(packageName, submittal.packageName) ||
          notEqual(packageNumber, submittal.packageNumber)
        )
          await editSharePointReference({
            variables: { input: { packageName, packageNumber, id } },
          })

        await edit({
          id,
          typeId: type.value.toString(),
          subtypeId: subtype?.value?.toString() ?? null,
          quantity,
          units,
          requestedReviewDate: requestedReviewDate.toISOString(),
          contractualReviewDate: contractualReviewDate?.toISOString() ?? null,
          routingGroupIds: routingGroups.map(({ value }) => value.toString()),
          externalLinks,
          payItemIds: fromPayItems(payItems),
          customFields: toMutateCustomFieldsInput(customFields),
        })
        refetch()
      },
    },
  }
}
